import React from 'react';
import { TruncatedCopy } from 'common/Text/TruncatedCopy';
import { RateCardModel } from '@utmost.co/enterprise-models';
import { DataTableTypes } from '@utmost.co/utmost-design-system';
import { useDeepMemo } from '@beeline.com/bp-design-system';
import { RateCardNumRates } from 'src/components/pages/settings/Engagements/RateCards/RateCardNumRates';
import { useFormatAddressAsSingleLine } from 'src/utils/useFormatAddressAsSingleLine';
import { RateCardRowActions } from './RateCardRowActions';
import { RateCardRowLink } from './RateCardRowLink';
import { useRateCardsIntl } from './translations/useRateCardsIntl';
import { RateCardSortTypeModel } from 'src/graphql/types';

interface Props {
  onDelete: (uid: string) => void;
}

export const useRateCardColumns = (props: Props): DataTableTypes.Column<RateCardModel & { '[menu]': string }>[] => {
  const intl = useRateCardsIntl();
  const formatAddressAsSingleLine = useFormatAddressAsSingleLine();

  return useDeepMemo(
    (): DataTableTypes.Column<RateCardModel & { '[menu]': string }>[] => [
      {
        Header: intl.name,
        accessor: 'name',
        id: RateCardSortTypeModel.Name,
        enableSorting: true,
        blockColumnHiding: true,
        Cell: (columns) => columns.row.original.uid && <RateCardRowLink rateCard={columns.row.original} />
      },
      {
        Header: intl.referenceId,
        accessor: 'referenceId',
        id: RateCardSortTypeModel.ReferenceId,
        enableSorting: true,
        defaultWidth: 300,
        ellipsisOnOverflow: false,
        Cell: ({ value }) => <TruncatedCopy textCopy={value ?? '-'} />
      },
      {
        Header: intl.referenceCode,
        accessor: 'referenceCode',
        id: RateCardSortTypeModel.ReferenceCode,
        enableSorting: true,
        defaultWidth: 300,
        ellipsisOnOverflow: false,
        Cell: ({ value }) => <TruncatedCopy textCopy={value ?? '-'} />
      },
      {
        Header: intl.jobProfile,
        accessor: 'jobProfile',
        Cell: ({ value }) => value?.name ?? '-'
      },
      {
        Header: intl.location,
        accessor: 'businessSiteOverrides',
        Cell: ({ value }) => value?.map((location) => location.name).join(', ') || 'All locations',
        defaultWidth: 300
      },
      {
        Header: intl.remoteWorkAddress,
        accessor: 'remoteWorkAddress',
        Cell: ({ value }) => formatAddressAsSingleLine(value) || '-',
        defaultWidth: 300
      },
      {
        Header: intl.supplier,
        accessor: 'supplier',
        Cell: ({ value }) => value?.name ?? '-',
        defaultWidth: 250
      },
      {
        Header: intl.rates,
        minWidth: 80,
        accessor: 'rates',
        Cell: (columns) => columns.row.original.uid && <RateCardNumRates rateCard={columns.row.original} />
      },
      {
        Header: '',
        id: 'menu',
        accessor: '[menu]',
        sticky: 'right',
        Cell: (columns) =>
          columns.row.original.uid && <RateCardRowActions rateCard={columns.row.original} onDelete={props.onDelete} />
      }
    ],
    [intl, formatAddressAsSingleLine, props.onDelete]
  );
};
